import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import type { IAiSpreadsheetSheet } from "@/payroll/ai-script/types";
import { EmptySheetState } from "./empty-sheet-state";
import { SpreadsheetGrid } from "./spreadsheet-grid";
import type { SpreadsheetViewer } from "./use-spreadsheet-viewer";

interface SheetGridAreaProps {
  viewer: SpreadsheetViewer;
  activeSheet: IAiSpreadsheetSheet;
  fillHeight: boolean;
  canAskAiAboutCell: boolean;
  canNoteFromSelection: boolean;
}

export const SheetGridArea = ({
  viewer,
  activeSheet,
  fillHeight,
  canAskAiAboutCell,
  canNoteFromSelection,
}: SheetGridAreaProps) => {
  const {
    ui: { isMaximized },
    sheet: {
      isTooNarrowForGrid,
      activeSheetError,
      isActiveSheetMissing,
      refetchActiveSheet,
      isFetchingActiveSheet,
      adaptedSheet,
    },
    isActiveSheetLoading,
    isActiveSheetEmpty,
    comments: { areCommentsEnabled },
  } = viewer;

  return (
    <Stack
      align="start"
      gap={0}
      className={cn("w-full", {
        "min-h-0 min-w-0 flex-1": isMaximized || fillHeight,
        "h-[600px]": !isMaximized && !fillHeight,
      })}
    >
      {isTooNarrowForGrid ? (
        <EmptySheetState
          title="Open this on a bigger screen"
          description="The Grid needs more room than a phone gives it. Open this page on a tablet or computer to read it."
          isFramed={false}
        />
      ) : activeSheetError ? (
        <Stack
          align="start"
          gap={4}
          justify="center"
          className="h-full w-full p-8"
        >
          <Alert color="danger">
            <AlertTitle>Unable to load {activeSheet.name}</AlertTitle>
            <AlertDescription>
              {activeSheetError instanceof Error
                ? activeSheetError.message
                : "This sheet is no longer available."}
            </AlertDescription>
          </Alert>
          {!isActiveSheetMissing && (
            <Button
              size="sm"
              disabled={isFetchingActiveSheet}
              onClick={() => void refetchActiveSheet()}
            >
              Try again
            </Button>
          )}
        </Stack>
      ) : isActiveSheetLoading ? (
        <Spinner
          layout="centered"
          aria-label={`Loading ${activeSheet.name}`}
          tone="primary"
          size="lg"
        />
      ) : isActiveSheetEmpty ? (
        <EmptySheetState
          title={`${activeSheet.name} is empty`}
          isFramed={false}
        />
      ) : adaptedSheet ? (
        <SpreadsheetGrid
          viewer={viewer}
          adaptedSheet={adaptedSheet}
          areCommentsEnabled={areCommentsEnabled}
          canAskAiAboutCell={canAskAiAboutCell}
          canNoteFromSelection={canNoteFromSelection}
        />
      ) : (
        <EmptySheetState
          title={`${activeSheet.name} is unavailable`}
          description="Choose another sheet or refresh the Grid."
          isFramed={false}
        />
      )}
    </Stack>
  );
};
